/**
 * @fileoverview Bidirectional adapters between non-canonical address shapes and
 *   the canonical PostalAddress contract.
 * @module core-domain/types/addressAdapters
 *
 * Responsibilities:
 * - Define the two non-canonical "stored" address shapes as documented mirrors
 *   so any package can convert to/from PostalAddress without importing from
 *   api/ or mobile-pwa directly.
 * - Provide pure adapter functions for each direction.
 *
 * Dependencies:
 * - PostalAddress (this package — no external dependencies).
 *
 * Architecture:
 * - Local-first safe: all adapters are pure functions with no network or
 *   storage access; usable in any context (service, hook, component, test).
 * - The "source of truth" for field names lives in the canonical PostalAddress
 *   contract. These adapters exist because changing every MongoDB document and
 *   API route at once is impractical — they provide an incremental convergence
 *   path: new code works with PostalAddress via adapters; a future data
 *   migration can retire the non-canonical shapes entirely.
 *
 * Notes:
 * - Two non-canonical shapes exist:
 *     1. LegacyDepartmentAddress (street1/zipCode/country) — used by the API
 *        Address type in system-api and api/core, and by mobile-pwa's Address.
 *     2. WizardDepartmentAddress (street/zip/country) — used by the system-web
 *        onboarding wizard and EditDepartmentModal (DepartmentAddress).
 * - Both shapes are the same logical data (a US mailing address) with
 *   different field names inherited from independent module histories.
 * - See `Docs/address-format-consistency-phase0-audit/02-address-shape-inventory.md`
 *   for the full cross-shape field mapping.
 */

import type { PostalAddress, PostalAddressValidationStatus } from './postalAddress';

// ─────────────────────────────────────────────────────────────────────────────
// Non-canonical shape 1: API / mobile-pwa "Address"
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @contract LegacyDepartmentAddress
 * Purpose: Mirror of the `Address` interface declared independently in
 *   `api/system-api/src/types/shared.ts` and `api/packages/core/src/shared.ts`.
 *   Also structurally matches `web/mobile-pwa/src/types/shared.ts` Address
 *   (sans GIS-imported fields like fullAddress/streetNumber/streetSuffix which
 *   are entity-location metadata, not mailing-address fields).
 * Fields:
 * - street1/street2: Primary and secondary address lines (PostalAddress: line1/line2).
 * - city, state: Same names as PostalAddress.
 * - zipCode: Postal code (PostalAddress: postalCode).
 * - county: Department-specific; not in PostalAddress (preserved through round-trips).
 * - country: Free-form country string (PostalAddress: countryCode, ISO).
 * Rules:
 * - Do NOT import the original api/ or mobile-pwa types into core-domain;
 *   this mirror exists so the adapter can be framework- and package-independent.
 * - Keep field-for-field aligned with the server-side Address type. If the
 *   server type changes, update this mirror and the adapter simultaneously.
 * Sync: Persisted on tenant/entity documents in MongoDB; synced to Dexie.
 */
export interface LegacyDepartmentAddress {
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  county?: string;
  country?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Non-canonical shape 2: system-web wizard/edit-modal "DepartmentAddress"
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @contract WizardDepartmentAddress
 * Purpose: Mirror of the `DepartmentAddress` interface used by system-web's
 *   `DepartmentAddressField.tsx` — the onboarding wizard and edit department
 *   modal. Field names (`street`/`zip`/`country`) differ from both PostalAddress
 *   AND LegacyDepartmentAddress because this shape evolved from early wizard
 *   form state before any canonical shape existed.
 * Fields:
 * - street: Primary address line (PostalAddress: line1, Legacy: street1).
 * - city, state: Same names as PostalAddress.
 * - zip: Postal code (PostalAddress: postalCode, Legacy: zipCode).
 * - county: Department-specific; not in PostalAddress.
 * - country: Free-form (PostalAddress: countryCode, ISO).
 * - validationStatus/validationMessage/validatedAt: USPS validation state,
 *   matching PostalAddress validation fields.
 * Rules:
 * - system-web's DepartmentAddressField.tsx should import from here rather
 *   than maintaining its own local copy of the adapter functions.
 * Sync: Persisted to the tenant document via the provisioning/update API.
 */
export interface WizardDepartmentAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  county?: string;
  country?: string;
  validationStatus?: PostalAddressValidationStatus;
  validationMessage?: string;
  validatedAt?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Adapters: LegacyDepartmentAddress ↔ PostalAddress
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @process legacyAddressToPostalAddress
 * Steps:
 * 1. Map LegacyDepartmentAddress fields to PostalAddress fields:
 *    street1→line1, street2→line2, zipCode→postalCode, country→countryCode.
 * 2. Set validationStatus to 'needsValidation' (legacy records have no
 *    validation state).
 * Inputs: A LegacyDepartmentAddress (partial).
 * Outputs: A PostalAddress with offline-default validation status.
 * Failure Handling: Pure; never throws. Missing fields default to empty strings.
 * Idempotency: REQUIRED (pure function).
 */
export function legacyAddressToPostalAddress(addr: LegacyDepartmentAddress): PostalAddress {
  return {
    line1: addr.street1 ?? '',
    line2: addr.street2 || undefined,
    city: addr.city ?? '',
    state: addr.state ?? '',
    postalCode: addr.zipCode ?? '',
    countryCode: addr.country ?? 'US',
    validationStatus: 'needsValidation',
  };
}

/**
 * @process postalAddressToLegacyAddress
 * Steps:
 * 1. Map PostalAddress fields back to LegacyDepartmentAddress fields:
 *    line1→street1, line2→street2, postalCode→zipCode, countryCode→country.
 * 2. Preserve the existing county value (not in PostalAddress).
 * Inputs: A PostalAddress (or undefined), plus optional county string.
 * Outputs: A LegacyDepartmentAddress.
 * Failure Handling: Pure; never throws.
 * Idempotency: REQUIRED (pure function).
 */
export function postalAddressToLegacyAddress(
  postal: PostalAddress | undefined,
  county?: string,
): LegacyDepartmentAddress {
  if (!postal) {
    return {
      street1: undefined,
      street2: undefined,
      city: undefined,
      state: undefined,
      zipCode: undefined,
      county,
      country: undefined,
    };
  }
  return {
    street1: postal.line1 || undefined,
    street2: postal.line2 || undefined,
    city: postal.city || undefined,
    state: postal.state || undefined,
    zipCode: postal.postalCode || undefined,
    county,
    country: postal.countryCode || undefined,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Adapters: WizardDepartmentAddress ↔ PostalAddress
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @process wizardAddressToPostalAddress
 * Steps:
 * 1. Map WizardDepartmentAddress fields to PostalAddress fields:
 *    street→line1, zip→postalCode, country→countryCode.
 * 2. Preserve validation fields if present; default to 'needsValidation'.
 * Inputs: A WizardDepartmentAddress (partial).
 * Outputs: A PostalAddress preserving validation status if present.
 * Failure Handling: Pure; never throws. Missing fields default to empty strings.
 * Idempotency: REQUIRED (pure function).
 */
export function wizardAddressToPostalAddress(addr: WizardDepartmentAddress): PostalAddress {
  return {
    line1: addr.street ?? '',
    city: addr.city ?? '',
    state: addr.state ?? '',
    postalCode: addr.zip ?? '',
    countryCode: addr.country ?? 'US',
    validationStatus: addr.validationStatus ?? 'needsValidation',
    validationMessage: addr.validationMessage,
    validatedAt: addr.validatedAt,
  };
}

/**
 * @process postalAddressToWizardAddress
 * Steps:
 * 1. Map PostalAddress fields back to WizardDepartmentAddress fields:
 *    line1→street, postalCode→zip, countryCode→country.
 * 2. Preserve the existing county value and validation fields.
 * Inputs: A PostalAddress (or undefined), plus optional county string.
 * Outputs: A WizardDepartmentAddress patch object.
 * Failure Handling: Pure; never throws.
 * Idempotency: REQUIRED (pure function).
 */
export function postalAddressToWizardAddress(
  postal: PostalAddress | undefined,
  county?: string,
): WizardDepartmentAddress {
  if (!postal) {
    return {
      street: undefined,
      city: undefined,
      state: undefined,
      zip: undefined,
      county,
      country: undefined,
      validationStatus: undefined,
      validationMessage: undefined,
      validatedAt: undefined,
    };
  }
  return {
    street: postal.line1 || undefined,
    city: postal.city || undefined,
    state: postal.state || undefined,
    zip: postal.postalCode || undefined,
    county,
    country: postal.countryCode || undefined,
    validationStatus: postal.validationStatus,
    validationMessage: postal.validationMessage,
    validatedAt: postal.validatedAt,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Cross-shape converter: LegacyDepartmentAddress ↔ WizardDepartmentAddress
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @process legacyAddressToWizardAddress
 * Steps:
 * 1. Map LegacyDepartmentAddress fields to WizardDepartmentAddress fields:
 *    street1→street, zipCode→zip.
 * Inputs: A LegacyDepartmentAddress.
 * Outputs: A WizardDepartmentAddress (without validation fields — legacy has none).
 * Failure Handling: Pure; never throws.
 * Idempotency: REQUIRED (pure function).
 */
export function legacyAddressToWizardAddress(addr: LegacyDepartmentAddress): WizardDepartmentAddress {
  return {
    street: addr.street1,
    city: addr.city,
    state: addr.state,
    zip: addr.zipCode,
    county: addr.county,
    country: addr.country,
  };
}

/**
 * @process wizardAddressToLegacyAddress
 * Steps:
 * 1. Map WizardDepartmentAddress fields to LegacyDepartmentAddress fields:
 *    street→street1, zip→zipCode.
 * Inputs: A WizardDepartmentAddress.
 * Outputs: A LegacyDepartmentAddress (validation fields are dropped — not
 *   present on the legacy shape).
 * Failure Handling: Pure; never throws.
 * Idempotency: REQUIRED (pure function).
 */
export function wizardAddressToLegacyAddress(addr: WizardDepartmentAddress): LegacyDepartmentAddress {
  return {
    street1: addr.street,
    street2: undefined,
    city: addr.city,
    state: addr.state,
    zipCode: addr.zip,
    county: addr.county,
    country: addr.country,
  };
}
